import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { debounce } from "lodash";
import {
  faChevronRight,
  faInfoCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, motion } from "framer-motion";
import mesurementImage from "../../../assets/imgs/user/medidas-perimetrales.jpg";
import perimetral_en from "../../../assets/imgs/user/en/medidas-perimetrales.jpg";
import perimetral_pt from "../../../assets/imgs/user/pt/medidas-perimetrales.jpg";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import {
  useAnthropometricChartQuery,
  useAnthropometricQuery,
  useGetNotesQuery,
  usePostAnthropometricMutation,
  usePostNotesMutation,
} from "@/app/users/athropometric.api";
import { DialogCreate } from "../molecules/DialogCreate";
import RenderFormUploadList from "./RenderFormUploadList";
import { BASE_URL } from "@/app/app.api";
import {
  useDeleteProgressImageMutation,
  useProgressImageQuery,
} from "@/app/progreso/progress.api";
import { useSelector } from "react-redux";
import { RootState } from "@/app/app.store";
import { useUsersQuery } from "@/app/users/users.api";
import i18n from "@/app/i18n";

const MeasurementSection = ({
  title,
  fields,
  infoIcon,
  iconAction,
  formik,
  handleInputChange,
  setChartContent,
}: any) => (
  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm mb-6">
    <div className="flex items-center gap-5 mb-4">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {infoIcon && (
        <FontAwesomeIcon
          onClick={iconAction}
          icon={faInfoCircle}
          className="h-4 w-4 text-primary cursor-pointer"
        />
      )}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {fields.map((field) => (
        <div
          key={field.name}
          className="flex flex-col sm:flex-row sm:items-center gap-2"
        >
          <label className="text-sm font-medium text-gray-700 sm:w-32 break-words">
            {field.label}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              name={field.name}
              value={formik.values[field.name]}
              onChange={handleInputChange}
              className={`w-20 p-2 border rounded-md text-right ${
                field.name === "fatPercentage"
                  ? "bg-gray-100 cursor-not-allowed"
                  : "focus:ring-2 focus:ring-primary"
              }`}
              placeholder="-"
              readOnly={field.name === "fatPercentage" || field?.disabled}
            />
            <span className="text-xs text-gray-600 w-8">{field.unit}</span>
            {field.showChart && field.name !== "fatPercentage" && (
              <button
                type="button"
                className={`text-gray-400 hover:text-gray-600 w-6 ${
                  formik.values[field.name] === ""
                    ? "opacity-0 pointer-events-none"
                    : "opacity-100"
                }`}
                onClick={() => setChartContent(field.name)}
              >
                <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const FormEvaluationAnthropometric = ({ user }: any) => {
  const { id } = useParams<{ id: string }>();
  const [modalContent, setModalContent] = useState<any>(null);
  const [modalPlicómetro, setmodalPlicómetro] = useState<any>(null);
  const [chartContent, setChartContent] = useState<any>(null);
  const [notesModal, setNotesModal] = useState(false);
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const [noteText, setNoteText] = useState("");
  const [timeGranularity, setTimeGranularity] = useState<string>("Month");
  const [selectedMonth, setSelectedMonth] = useState<string>("April");
  const [selectedYear, setSelectedYear] = useState<string>("2025");
  const {
    data: ListImages,
    isLoading: isLoadingImages,
    refetch: refetchImages,
  } = useProgressImageQuery({ id: id });

  const { data: noteData } = useGetNotesQuery({ id: id });

  const [postNotes, { isLoading: isLoadingPostNotes }] = usePostNotesMutation();

  const [
    deleteProgressImage,
    { isLoading: isLoadingDeleteProgressImage, originalArgs },
  ] = useDeleteProgressImageMutation();

  const timeOptions = ["Month", "Year"];
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  interface FatPercentageParams {
    height: string; // Altura en cm
    sex: "hombre" | "mujer";
    waist: string; // Cintura en cm
    neck: string; // Cuello en cm
    hip?: string; // Cadera en cm (opcional, solo para mujeres)
  }
  const calculateFatPercentage = useCallback(
    ({ height, sex, waist, neck, hip }: FatPercentageParams) => {
      const heightNum = parseFloat(height);
      const waistNum = parseFloat(waist);
      const neckNum = parseFloat(neck);
      const hipNum = hip ? parseFloat(hip) : 0;

      // Convertir de cm a pulgadas (1 cm = 0.393701 pulgadas)
      const cmToInches = 0.393701;
      const heightInches = heightNum * cmToInches;
      const waistInches = waistNum * cmToInches;
      const neckInches = neckNum * cmToInches;
      const hipInches = hipNum * cmToInches;

      if (
        heightNum &&
        waistNum &&
        neckNum &&
        (sex === "hombre" || (sex === "mujer" && hipNum))
      ) {
        let estimatedFat: number;

        if (sex === "hombre") {
          // Fórmula para hombres
          estimatedFat =
            86.01 * Math.log10(waistInches - neckInches) -
            70.041 * Math.log10(heightInches) +
            36.76;
        } else {
          // Fórmula para mujeres
          estimatedFat =
            163.205 * Math.log10(waistInches + hipInches - neckInches) -
            97.684 * Math.log10(heightInches) -
            78.387;
        }

        return estimatedFat.toFixed(1);
      }

      return "";
    },
    []
  );

  const [postAnthropometric, { isLoading }] = usePostAnthropometricMutation();

  const formik = useFormik({
    initialValues: {
      height: "",
      weight: "",
      fatPercentage: "",
      arm: "",
      neck: "",
      waist: "",
      hip: "",
      leg: "",
      chest: "",
      shoulder: "",
      imc: "",
      muscleMass: "",
      boneMass: "",
      fatManual: "",
      visceralFat: "",
      metabolicAge: "",
      bodyWater: "",
      tricipital: "",
      subscapular: "12",
      bicipital: "",
      iliacCrest: "",
      abdominal: "",
      suprascapular: "",
      frontThigh: "",
      observations: "",
      SkinfoldFatWeight: "",
      SkinfoldLeanMassIndex: "",
    },
    onSubmit: async (values) => {
      console.log("Form submitted:", values);
    },
  });

  const userData = user?.data;

  const { data } = useAnthropometricChartQuery({ id });

  const { data: nthropometriQuery } = useAnthropometricQuery({ id });
  console.log("Anthropometric data:", data);

  useEffect(() => {
    if (nthropometriQuery) {
      formik.setValues({
        height: nthropometriQuery?.height || "",
        weight: nthropometriQuery?.weight || "",
        fatPercentage: calculateFatPercentage({
          height: nthropometriQuery?.height || "",
          sex: userData?.sex?.toLowerCase(),
          waist: nthropometriQuery?.waist || "",
          neck: nthropometriQuery?.neck || "",
        }),
        arm: nthropometriQuery?.arm || "",
        neck: nthropometriQuery?.neck || "",
        waist: nthropometriQuery?.waist || "",
        hip: nthropometriQuery?.hip || "",
        leg: nthropometriQuery?.leg || "",
        chest: nthropometriQuery?.chest || "",
        shoulder: nthropometriQuery?.shoulder || "",
        imc: nthropometriQuery?.imc || "",
        muscleMass: nthropometriQuery?.muscleMass || "",
        boneMass: nthropometriQuery?.boneMass || "",
        fatManual: nthropometriQuery?.fatManual || "",
        visceralFat: nthropometriQuery?.visceralFat || "",
        metabolicAge: nthropometriQuery?.metabolicAge || "",
        bodyWater: nthropometriQuery?.bodyWater || "",
        tricipital: nthropometriQuery?.tricipital || "",
        subscapular: nthropometriQuery?.subscapular || "",
        bicipital: nthropometriQuery?.bicipital || "",
        iliacCrest: nthropometriQuery?.fatPercentage || "",
        abdominal: nthropometriQuery?.abdominal || "",
        suprascapular: nthropometriQuery?.suprascapular || "",
        frontThigh: nthropometriQuery?.frontThigh || "",
        observations: nthropometriQuery?.observations || "",
        SkinfoldFatWeight: nthropometriQuery?.SkinfoldFatWeight || "",
        SkinfoldLeanMassIndex: nthropometriQuery?.SkinfoldLeanMassIndex || "",
      });
    }
  }, [nthropometriQuery]);

  useEffect(() => {
    if (noteData?.data) {
      setNoteText(noteData?.data || "");
    }
  }, [noteData]);

  useEffect(() => {
    if (noteText) {
      postNotes({
        id: id,
        notesData: {
          notes: noteText,
        },
      });
    }
  }, [id, noteText]);

  const debouncedPostAnthropometric = useCallback(
    debounce((id, values) => {
      postAnthropometric({
        id,
        anthropometricData: values,
      });
    }, 500), // Retrasa 500ms
    [postAnthropometric]
  );

  useEffect(() => {
    debouncedPostAnthropometric(id, formik.values);
  }, [id, formik.values, debouncedPostAnthropometric]);

  const handleInputChange = (e) => {
    formik.handleChange(e); // Maneja el cambio normal de Formik
    const { name, value } = e.target;
    if (name === "height" || name === "waist" || name === "neck") {
      const fatPercentage = calculateFatPercentage({
        height: name === "height" ? value : formik.values.height,
        sex: userData?.sex?.toLowerCase(),
        waist: name === "waist" ? value : formik.values.waist,
        neck: name === "neck" ? value : formik.values.neck,
        hip: formik.values.hip,
      });
      formik.setFieldValue("fatPercentage", fatPercentage, false);
    }
  };
  useEffect(() => {
    const fatPercentage = calculateFatPercentage({
      height: formik.values.height,
      sex: userData?.sex?.toLowerCase(),
      waist: formik.values.waist,
      neck: formik.values.neck,
      hip: formik.values.hip,
    });
    formik.setFieldValue("fatPercentage", fatPercentage, false);
  }, [formik.values.height, formik.values.weight, calculateFatPercentage]);

  const handleNotesSubmit = () => {
    console.log("Note saved:", noteText);
    setNotesModal(false);
  };

  const getSampleData = (
    fieldName: string,
    granularity: string,
    month?: string,
    year?: string
  ) => {
    if (!data || !data[fieldName] || !data[fieldName].chart) {
      console.log(`No chart data for ${fieldName}`);
      return [];
    }
  
    const chartData = data[fieldName].chart;
  
    // Filter out invalid entries
    const validChartData = chartData.filter((entry) => {
      const isValid =
        entry.date &&
        !isNaN(Date.parse(entry.date)) &&
        entry.value != null &&
        !isNaN(parseFloat(entry.value));
      if (!isValid) {
        console.warn(`Invalid chart entry for ${fieldName}:`, entry);
      }
      return isValid;
    });
  
    // Log raw valid data for debugging
    console.log(`Valid chart data for ${fieldName}:`, validChartData);
  
    // Remove duplicates by averaging values for the same date
    const uniqueData = validChartData.reduce((acc, curr) => {
      const dateKey = curr.date;
      if (!acc[dateKey]) {
        acc[dateKey] = { date: curr.date, value: 0, count: 0 };
      }
      acc[dateKey].value += parseFloat(curr.value) || 0;
      acc[dateKey].count += 1;
      return acc;
    }, {});
  
    let formattedData = Object.values(uniqueData).map((item: any) => ({
      date: item.date,
      value: item.count > 0 ? item.value / item.count : item.value,
    }));
  
    if (granularity === "Month" && month && year) {
      const monthIndex = months.indexOf(month);
      if (monthIndex !== -1) {
        formattedData = formattedData.filter((entry) => {
          const date = new Date(entry.date);
          return (
            date.getMonth() === monthIndex &&
            date.getFullYear().toString() === year
          );
        });
        // Aggregate by day
        const aggregatedData = formattedData.reduce((acc, curr) => {
          const date = new Date(curr.date);
          const key = `${date.getDate()}`;
          if (!acc[key]) {
            acc[key] = { category: key, value: 0, count: 0 };
          }
          acc[key].value += curr.value;
          acc[key].count += 1;
          return acc;
        }, {});
  
        let result = Object.values(aggregatedData).map((item: any) => ({
          category: item.category,
          value: item.count > 0 ? item.value / item.count : item.value,
        }));
  
        // Sort by day
        result.sort((a, b) => parseInt(a.category) - parseInt(b.category));
        console.log(`Month mode result for ${fieldName} (${month}, ${year}):`, result);
        return result;
      }
    } else if (granularity === "Year" && year) {
      // Filter data for the selected year
      formattedData = formattedData.filter((entry) => {
        const date = new Date(entry.date);
        return date.getFullYear().toString() === year;
      });
  
      // Log filtered data for debugging
      console.log(`Filtered data for ${fieldName} (Year ${year}):`, formattedData);
  
      // Aggregate by month
      const aggregatedData = formattedData.reduce((acc, curr) => {
        const date = new Date(curr.date);
        const monthIndex = date.getMonth();
        const key = months[monthIndex];
        if (!acc[key]) {
          acc[key] = { category: key, value: 0, count: 0 };
        }
        acc[key].value += curr.value;
        acc[key].count += 1;
        return acc;
      }, {});
  
      // Create an array with all months
      const result = months.map((month) => ({
        category: month,
        value: aggregatedData[month]
          ? aggregatedData[month].count > 0
            ? aggregatedData[month].value / aggregatedData[month].count
            : 0
          : 0,
      }));
  
      console.log(`Year mode result for ${fieldName} (Year ${year}):`, result);
      return result;
    }
  
    console.log(`No data after filtering for ${fieldName} (${granularity}, ${month || year})`);
    return [];
  };

  const createAreaChart = (
    fieldName: string,
    granularity: string,
    month?: string,
    year?: string
  ) => {
    const root = am5.Root.new("chart-area");
  
    root.setThemes([am5themes_Animated.new(root)]);
    if (root._logo) {
      root._logo.dispose();
    }
  
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: undefined,
        wheelY: undefined,
        pinchZoomX: true,
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 10,
        paddingBottom: 10,
        layout: root.verticalLayout, // Ensure consistent layout
      })
    );
  
    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
    cursor.lineY.set("visible", false);
  
    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 30,
      minorGridEnabled: true,
    });
  
    xRenderer.labels.template.setAll({
      rotation: 45,
      centerY: am5.p50,
      centerX: am5.p50,
      paddingRight: 15,
      fontSize: 12,
    });
  
    xRenderer.grid.template.setAll({
      location: 1,
      visible: false,
    });
  
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        maxDeviation: 0.3,
        categoryField: "category",
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {}),
      })
    );
  
    if (granularity === "Month") {
      xAxis.get("renderer").labels.template.adapters.add("text", (text) => {
        return text;
      });
    } else if (granularity === "Year") {
      xAxis.get("renderer").labels.template.adapters.add("text", (text) => {
        return text.substring(0, 3);
      });
    }
  
    const yRenderer = am5xy.AxisRendererY.new(root, {
      strokeOpacity: 0.1,
    });
  
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yRenderer,
        min: 0,
        numberFormat: "#",
      })
    );
  
    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color(0x6b7280),
      fontSize: 12,
    });
  
    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color(0x6b7280),
      fontSize: 12,
    });
  
    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: fieldName,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "value",
        sequencedInterpolation: true,
        categoryXField: "category",
        tooltip: am5.Tooltip.new(root, {
          labelText: `${fieldName}: {valueY}\n${granularity === "Year" ? "Month" : "Day"}: {category}`,
        }),
        fill: am5.color("#68478D"),
      })
    );
  
    series.bullets.push(() => {
      return am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: granularity === "Year" ? 7 : 5,
          fill: am5.color("#68478D"),
          stroke: am5.color("#ffffff"),
          strokeWidth: 2,
        }),
      });
    });
  
    series.fills.template.setAll({
      fillOpacity: 0.5,
      visible: true,
    });
  
    series.strokes.template.setAll({
      strokeWidth: 2,
      stroke: am5.color("#68478D"),
    });
  
    const chartData = getSampleData(fieldName, granularity, month, year);
  
    // Debug logging to trace data issues
    console.log(`Chart data for ${fieldName} (${granularity}, ${month || year}):`, chartData);
  
    // Check if chartData is empty or contains only zero/null values
    const hasValidData = chartData.some((item) => item.value !== 0 && item.value != null);
  
    if (!chartData.length || !hasValidData) {
      // Clear chart elements to avoid overlap
      chart.series.clear();
      chart.xAxes.clear();
      chart.yAxes.clear();
  
      // Add centered "No data available" message
      const label = chart.children.push(
        am5.Label.new(root, {
          text: t("anthropometric.no_data_available"), // Localized message
          centerX: am5.percent(50), // Explicitly center horizontally
          centerY: am5.percent(50), // Explicitly center vertically
          x: am5.percent(50),
          y: am5.percent(50),
          fontSize: 18,
          fontWeight: "500",
          fill: am5.color(0x6b7280), // Gray color for visibility
          textAlign: "center",
          background: am5.Rectangle.new(root, {
            fill: am5.color(0xffffff), // White background
            fillOpacity: 0.9, // Slightly more opaque
            stroke: am5.color(0xd1d5db), // Light gray border
            strokeWidth: 1,
          }),
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 20,
          paddingRight: 20,
        })
      );
  
      // Ensure the label is centered in the chart area
      label.adapters.add("x", (x) => {
        return root.width() / 2;
      });
      label.adapters.add("y", (y) => {
        return root.height() / 2;
      });
  
      console.log(`No valid data for ${fieldName}: Displaying centered 'No data available' message`);
      return root;
    }
  
    xAxis.data.setAll(chartData);
    series.data.setAll(chartData);
  
    series.appear(1000);
    chart.appear(1000, 100);
  
    return root;
  };
  

  useEffect(() => {
    if (chartContent) {
      const root = createAreaChart(
        chartContent,
        timeGranularity,
        selectedMonth,
        selectedYear
      );
      return () => {
        root.dispose();
      };
    }
  }, [chartContent, timeGranularity, selectedMonth, selectedYear]);

  const evolutionOpeExternalUri = (url: string) => {
    window.open(url, "_blank");
  };

  // Generate available years from data
  const availableYears = Array.from(
    new Set(
      Object.values(data || {})
        .filter((field: any) => field?.chart?.length > 0)
        .flatMap((field: any) => field.chart)
        .map((entry: any) => new Date(entry.date).getFullYear().toString())
    )
  ).sort();

  /*  if(isLoading){
    return <div>Loading...</div>
  } */

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="w-full h-full p-4 flex flex-col overflow-auto"
    >
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Measurements */}
        <div className="space-y-6">
          <MeasurementSection
            formik={formik}
            setChartContent={setChartContent}
            handleInputChange={handleInputChange}
            title={t("user.views.basic_measurements")}
            fields={[
              {
                name: "height",
                label: t("training-wizard.views.height"),
                unit: "cm",
                showChart: true,
              },
              {
                name: "weight",
                label: t("training-wizard.views.weight"),
                unit: "kg",
                showChart: true,
              },
              {
                name: "fatPercentage",
                label: t("user.views.fat_percentage"),
                unit: "%",
                showChart: false,
              },
            ]}
          />
          <MeasurementSection
            setChartContent={setChartContent}
            formik={formik}
            handleInputChange={handleInputChange}
            infoIcon={faInfoCircle}
            iconAction={() =>
              setModalContent({
                image:
                  i18n.language === "en"
                    ? perimetral_en
                    : i18n.language === "pt"
                      ? perimetral_pt
                      : mesurementImage,
                type: 1,
              })
            }
            title={t("user.views.measure")}
            fields={[
              {
                name: "arm",
                label: t("user.views.arm"),
                unit: "cm",
                showChart: true,
              },
              {
                name: "neck",
                label: t("user.views.neck"),
                unit: "cm",
                showChart: true,
              },
              {
                name: "waist",
                label: t("user.views.waist"),
                unit: "cm",
                showChart: true,
              },
              {
                name: "hip",
                label: t("user.views.hip"),
                unit: "cm",
                showChart: true,
              },
              {
                name: "leg",
                label: t("user.views.leg"),
                unit: "cm",
                showChart: true,
              },
              {
                name: "chest",
                label: t("user.views.chest"),
                unit: "cm",
                showChart: true,
              },
              {
                name: "shoulder",
                label: t("user.views.shoulder"),
                unit: "cm",
                showChart: true,
              },
            ]}
          />
          <MeasurementSection
            formik={formik}
            setChartContent={setChartContent}
            handleInputChange={handleInputChange}
            title={t("bioimpedancia")}
            fields={[
              {
                name: "imc",
                label: t("user.views.imc"),
                unit: "",
                showChart: true,
              },
              {
                name: "muscleMass",
                label: t("user.views.muscle_mass"),
                unit: "kg",
                showChart: true,
              },
              {
                name: "boneMass",
                label: t("user.views.bone_mass"),
                unit: "kg",
                showChart: true,
              },
              {
                name: "fatManual",
                label: t("user.views.fat_manual"),
                unit: "%",
                showChart: true,
              },
              {
                name: "visceralFat",
                label: t("user.views.visceral_fat"),
                unit: "kg",
                showChart: true,
              },
              {
                name: "metabolicAge",
                label: t("user.views.metabolic_age"),
                unit: "",
                showChart: true,
              },
              {
                name: "bodyWater",
                label: t("user.views.body_water"),
                unit: "kg",
                showChart: true,
              },
            ]}
          />
        </div>

        {/* Right Column - Lifestyle and Questions */}
        <div className="space-y-6">
          <MeasurementSection
            formik={formik}
            iconAction={() => setModalContent({ image: null, type: 2 })}
            infoIcon={faInfoCircle}
            setChartContent={setChartContent}
            handleInputChange={handleInputChange}
            title={t("plicometro")}
            fields={[
              {
                name: "tricipital",
                label: t("user.views.tricipital"),
                unit: "mm",
                showChart: true,
              },
              {
                name: "subscapular",
                label: t("user.views.subscapularis"),
                unit: "mm",
                showChart: true,
              },

              {
                name: "iliacCrest",
                label: t("user.views.fat_skinfold"),
                unit: "mm",
                showChart: true,
              },
              {
                name: "abdominal",
                label: t("user.views.abdominal"),
                unit: "mm",
                showChart: true,
              },
              {
                name: "suprascapular",
                label: t("user.views.supraspinal"),
                unit: "mm",
                showChart: true,
              },
              {
                name: "frontThigh",
                label: t("user.views.front_thigh"),
                unit: "mm",
                showChart: true,
              },

              {
                name: "SkinfoldFatWeight",
                label: "Masa magra estimada",
                unit: "kg",
                showChart: true,
                disabled: true,
              },

              {
                name: "SkinfoldLeanMassIndex",
                label: "Índice de masa magra",
                showChart: true,
                disabled: true,
              },
            ]}
          />
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm">
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <button
                type="button"
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 w-full sm:w-auto"
                onClick={() => setUploadModal(true)}
              >
                Imágenes del progreso del usuario
                {t("anthropometric.progress.image")}
              </button>
              <a
                href={`${BASE_URL}/admin/users/${id}/assessment/anthropometric`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <button
                  type="button"
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 w-full sm:w-auto"
                >
                  {t("anthropometric.user_evaluation")}
                </button>
              </a>

              <button
                type="button"
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 w-full sm:w-auto"
                onClick={() => setNotesModal(true)}
              >
                {t("anthropometric.notes")}
              </button>
            </div>
            <div className="mt-6">
              <h3 className="text-md font-semibold text-gray-900 mb-2">
                {t("anthropometric.observations")}
              </h3>
              <textarea
                name="observations"
                value={formik.values.observations}
                onChange={formik.handleChange}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder={t("anthropometric.observations_placeholder")}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Image */}
      <AnimatePresence>
        {modalContent && (
          <motion.div
            className="fixed inset-0 flex justify-end z-50 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) =>
              e.target === e.currentTarget && setModalContent(null)
            }
          >
            <motion.div
              className="w-full sm:w-[800px] h-full bg-white shadow-xl overflow-y-auto border-l border-gray-300"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
            >
              <button
                type="button"
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 z-10"
                onClick={() => setModalContent(null)}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <div>
                <div className="w-full">
                  <img
                    src={modalContent?.image && modalContent?.image}
                    alt="Measurement guide"
                    className="w-full h-[500px] sm:h-[600px] object-contain"
                  />
                </div>

                <div className="p-6 space-y-4 text-sm sm:text-base text-gray-800">
                  {modalContent && modalContent?.type === 2 && (
                    <>
                      <p className="font-semibold">
                        {t("anthropometric.medidas_pliegues.text_2")}
                      </p>
                      <p>{t("anthropometric.medidas_pliegues.text_1")}</p>

                      <p className="font-semibold">
                        {t("anthropometric.medidas_pliegues.text_3")}
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>{t("anthropometric.medidas_pliegues.text_4")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_5")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_6")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_7")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_8")}</li>
                      </ul>

                      <p className="font-semibold">
                        {t("anthropometric.medidas_pliegues.text_9")}
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>{t("anthropometric.medidas_pliegues.text_10")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_11")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_12")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_13")}</li>
                      </ul>
                    </>
                  )}

                  {modalContent && modalContent?.type === 1 && (
                    <>
                      {/* permitre que el usuario pueda ver la imagen de la medida plico */}
                      <p className="font-semibold">
                        {t("anthropometric.medidas_pliegues.text_14")}
                      </p>
                      <p>{t("anthropometric.medidas_pliegues.text_15")}</p>
                      <p className="font-semibold">
                        {t("anthropometric.medidas_pliegues.text_16")}
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>{t("anthropometric.medidas_pliegues.text_17")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_18")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_19")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_20")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_21")}</li>
                      </ul>

                      <p className="font-semibold">
                        {t("anthropometric.medidas_pliegues.text_22")}
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>{t("anthropometric.medidas_pliegues.text_23")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_24")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_25")}</li>
                        <li>{t("anthropometric.medidas_pliegues.text_26")}</li>
                      </ul>

                      <p>{t("anthropometric.medidas_pliegues.text_27")}</p>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chart Modal */}
      <AnimatePresence>
        {chartContent && (
          <motion.div
            className="fixed inset-0 flex justify-center items-center z-50 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) =>
              e.target === e.currentTarget && setChartContent(null)
            }
          >
            <motion.div
              className="w-full sm:w-[900px] h-[600px] bg-white rounded-xl shadow-2xl border border-gray-300 flex flex-col"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <div className="p-6 flex justify-between items-center border-b">
                <h3 className="text-xl font-semibold text-gray-900">
                  Chart for {chartContent}
                </h3>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-800"
                  onClick={() => setChartContent(null)}
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="px-6 py-4 flex gap-4 items-center">
  <div className="w-1/3">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Time Granularity
    </label>
    <select
      value={timeGranularity}
      onChange={(e) => {
        setTimeGranularity(e.target.value);
        if (e.target.value === "Month" && !selectedMonth) {
          setSelectedMonth("April");
          setSelectedYear("2025");
        } else if (e.target.value === "Year" && !selectedYear) {
          setSelectedYear("2025");
        }
      }}
      className="w-full p-2 rounded-md focus:ring-2"
    >
      {timeOptions.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </div>
  {timeGranularity === "Month" && (
    <div className="w-1/3">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Month
      </label>
      <select
        value={selectedMonth}
        onChange={(e) => setSelectedMonth(e.target.value)}
        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
      >
        <option value="">Select Month</option>
        {months.map((month) => (
          <option key={month} value={month}>
            {month}
          </option>
        ))}
      </select>
    </div>
  )}
  <div className="w-1/3">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Year
    </label>
    <select
      value={selectedYear}
      onChange={(e) => setSelectedYear(e.target.value)}
      className="w-full p-2 border rounded-md focus:ring-2"
    >
      {availableYears.map((year) => (
        <option key={year} value={year}>
          {year}
        </option>
      ))}
    </select>
  </div>
</div>
              <div className="flex-1 px-6 pb-6 pt-0">
                <div
                  id="chart-area"
                  style={{ width: "100%", height: "98%", margin: "10px" }}
                ></div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notes Modal */}
      <AnimatePresence>
        {notesModal && (
          <motion.div
            className="fixed inset-0 flex justify-center items-center z-50 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) =>
              e.target === e.currentTarget && setNotesModal(false)
            }
          >
            <motion.div
              className="w-full sm:w-[500px] bg-white p-6 rounded-xl shadow-2xl border border-gray-300 relative"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <button
                type="button"
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
                onClick={() => setNotesModal(false)}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t("anthropometric.notes_modal_title")}
              </h3>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                rows={6}
              />
              <div className="flex justify-end mt-4 gap-4">
                <button
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  onClick={() => setNotesModal(false)}
                >
                  {t("general.cancel")}
                </button>
                <button
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary"
                  onClick={handleNotesSubmit}
                >
                  {t("general.save")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <DialogCreate
        tittle={t("anthropometric.upload_image")}
        tittleButtonCancel={t("general.cancel")}
        tittleButtonCreate={t("general.create")}
        open={uploadModal}
        setOpen={(open) => setUploadModal(open)}
        /* setOpen={(open) => setOpenPreview({ position: null, state: open })} */
        OnclickCancel={() => setUploadModal(false)}
      >
        <RenderFormUploadList
          accept="image/*"
          enpoint="/admin/progress-image/create"
          listImage={ListImages?.data ?? []}
          listLoader={isLoadingImages}
          refetchList={refetchImages}
          user_id={id}
          deleteAction={({ image_id }) => deleteProgressImage({ id, image_id })}
        />
      </DialogCreate>
    </form>
  );
};

export default FormEvaluationAnthropometric;
